import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;

function buildEmailHtml(event, vars = {}) {
  const eventUrl = `${window.location.origin}/event/${event.slug}`;
  const companyUrl = `${window.location.origin}/event/${event.slug}/companies`;
  const startDate = event.start_date ? new Date(event.start_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';
  const endDate = event.end_date ? new Date(event.end_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';
  const dateRange = endDate ? `${startDate} — ${endDate}` : startDate;
  const firstName = vars.firstName || 'there';

  return `
<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"></head>
<body style="margin:0;padding:0;background:#f8fafc;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;">
<table width="100%" cellpadding="0" cellspacing="0" style="background:#f8fafc;padding:40px 20px;">
<tr><td align="center">
<table width="600" cellpadding="0" cellspacing="0" style="background:white;border-radius:16px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.06);">

<tr><td style="background:linear-gradient(135deg,#0F172A,#1E293B);padding:40px 40px 30px;text-align:center;">
  ${event.logo_image_url ? `<img src="${event.logo_image_url}" alt="" style="height:48px;margin-bottom:16px;">` : ''}
  <h1 style="color:white;font-size:28px;font-weight:700;margin:0 0 8px;font-family:Georgia,serif;">${event.event_name}</h1>
  ${event.tagline ? `<p style="color:#c9a84c;font-size:16px;margin:0;">${event.tagline}</p>` : ''}
</td></tr>

<tr><td style="padding:36px 40px;">
  <p style="font-size:16px;color:#0F172A;line-height:1.6;margin:0 0 20px;">
    ${firstName},
  </p>
  <p style="font-size:16px;color:#334155;line-height:1.6;margin:0 0 20px;">
    This is your official invitation to the <strong>${event.event_name}</strong>!
  </p>
  <p style="font-size:16px;color:#334155;line-height:1.6;margin:0 0 24px;">
    You're receiving this invite because you or a member of your team expressed interest in meeting fresh toy and game creators — and we'd be honored to have you join us.
  </p>

  <table width="100%" cellpadding="0" cellspacing="0" style="background:#f8fafc;border-radius:12px;padding:24px;margin-bottom:24px;">
  <tr><td>
    <p style="font-size:14px;font-weight:600;color:#0F172A;margin:0 0 12px;">Event Details</p>
    ${dateRange ? `<p style="font-size:14px;color:#475569;margin:0 0 6px;">📅 Dates: ${dateRange}</p>` : ''}
    ${event.time_commitment ? `<p style="font-size:14px;color:#475569;margin:0 0 6px;">⏱ Time Commitment: ${event.time_commitment}</p>` : ''}
    ${event.company_price ? `<p style="font-size:14px;color:#475569;margin:0;">💰 Cost: ${event.company_price}</p>` : ''}
  </td></tr>
  </table>

  <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:16px;">
  <tr><td align="center">
    <a href="${companyUrl}" style="display:inline-block;background:linear-gradient(135deg,#c9a84c,#F59E0B);color:white;padding:14px 32px;border-radius:999px;font-size:16px;font-weight:600;text-decoration:none;">
      ${event.booking_cta_text || 'Reserve Your Time Slot'}
    </a>
  </td></tr>
  </table>

  <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:24px;">
  <tr><td align="center">
    <a href="${eventUrl}" style="display:inline-block;color:#c9a84c;font-size:14px;font-weight:600;text-decoration:none;">
      View Full Event Details →
    </a>
    ${event.event_password ? `<p style="font-size:12px;color:#94A3B8;margin:8px 0 0;">Password: ${event.event_password}</p>` : ''}
  </td></tr>
  </table>

  ${event.company_value_prop ? `
  <p style="font-size:16px;color:#334155;line-height:1.6;margin:0 0 16px;">${event.company_value_prop}</p>
  ` : ''}

  ${event.description ? `
  <p style="font-size:14px;color:#64748B;line-height:1.6;margin:0 0 24px;">${event.description}</p>
  ` : ''}

  <p style="font-size:14px;color:#64748B;line-height:1.6;margin:24px 0 0;">
    If you have questions or want to chat before booking, just reply to this email.
  </p>
</td></tr>

<tr><td style="border-top:1px solid #E2E8F0;padding:24px 40px;text-align:center;">
  <p style="font-size:12px;color:#94A3B8;margin:0;">
    Powered by <span style="color:#c9a84c;font-weight:600;">PlayVentr</span>
  </p>
</td></tr>

</table>
</td></tr>
</table>
</body>
</html>`;
}

export default function CompanyInviteEmailer({ event }) {
  const { user } = useAuth();
  const [emails, setEmails] = useState('');
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [gmailConnected, setGmailConnected] = useState(null);

  // Check Gmail connection
  useState(() => {
    const check = async () => {
      if (!user) return;
      const { data } = await supabase
        .from('gmail_connections')
        .select('gmail_email')
        .eq('user_id', user.id)
        .maybeSingle();
      setGmailConnected(data);
    };
    check();
  }, [user]);

  const parseEmails = () => {
    return emails
      .split(/[,\n;]+/)
      .map(e => e.trim())
      .filter(e => e && e.includes('@'));
  };

  const handleSend = async () => {
    const emailList = parseEmails();
    if (emailList.length === 0) return;

    setSending(true);
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    let sent = 0;
    let failed = 0;

    for (const email of emailList) {
      const firstName = email.split('@')[0].split('.')[0];
      const capitalizedName = firstName.charAt(0).toUpperCase() + firstName.slice(1);
      const htmlBody = buildEmailHtml(event, { firstName: capitalizedName });
      const subject = `You're Invited: ${event.event_name}${event.start_date ? ` — ${new Date(event.start_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}` : ''}`;

      try {
        const res = await fetch(`${SUPABASE_URL}/functions/v1/gmail-send`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ to: email, subject, htmlBody }),
        });

        if (res.ok) {
          sent++;
        } else {
          failed++;
        }
      } catch {
        failed++;
      }
    }

    setResults({ sent, failed, total: emailList.length });
    setSending(false);
  };

  if (!gmailConnected) {
    return (
      <div className="glass p-7">
        <h2 className="font-heading text-xl text-vault-navy mb-4">Send Company Invitations</h2>
        <div className="bg-amber-50 border border-amber-200 rounded-btn p-4">
          <p className="text-sm text-amber-700">Connect your Gmail account in Settings to send email invitations.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="glass p-7">
      <h2 className="font-heading text-xl text-vault-navy mb-1">Send Company Invitations</h2>
      <p className="text-sm text-gray-400 mb-5">Sending from: {gmailConnected.gmail_email}</p>

      {previewMode ? (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-vault-navy text-sm">Email Preview</h3>
            <button onClick={() => setPreviewMode(false)} className="text-vault-gold text-sm font-semibold">← Back to Editor</button>
          </div>
          <div className="border border-vault-border rounded-btn overflow-hidden">
            <iframe
              srcDoc={buildEmailHtml(event, { firstName: 'Sarah' })}
              title="Email Preview"
              className="w-full h-[600px]"
            />
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <div>
            <label className="block text-[12px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
              Company Emails (comma, semicolon, or newline separated)
            </label>
            <textarea
              className="w-full px-4 py-3 rounded-input border-2 border-vault-border text-sm font-body text-vault-navy bg-white/70 focus:border-vault-gold focus:outline-none min-h-[120px]"
              value={emails}
              onChange={e => setEmails(e.target.value)}
              placeholder="sarah@toycompany.com, mike@gamesltd.com"
            />
            <p className="text-xs text-gray-400 mt-1">{parseEmails().length} email(s) detected</p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setPreviewMode(true)}
              className="px-5 py-2.5 rounded-btn text-sm font-semibold border-2 border-vault-border text-vault-navy hover:bg-gray-50"
            >
              Preview Email
            </button>
            <button
              onClick={handleSend}
              disabled={sending || parseEmails().length === 0}
              className="px-5 py-2.5 rounded-btn text-sm font-semibold text-white shadow-btn disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
            >
              {sending ? 'Sending...' : `Send to ${parseEmails().length} Contact${parseEmails().length !== 1 ? 's' : ''}`}
            </button>
          </div>

          {results && (
            <div className={`p-4 rounded-btn ${results.failed > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-emerald-50 border border-emerald-200'}`}>
              <p className={`text-sm font-semibold ${results.failed > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                {results.sent} of {results.total} emails sent successfully
                {results.failed > 0 ? ` (${results.failed} failed)` : ''}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
